import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  keyframes,
} from "@mui/material";
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ShieldIcon from '@mui/icons-material/Shield';
import { DATABASE } from "../../../Config/firebase";
import { collection, query, where, getDocs, limit, orderBy } from "firebase/firestore";
import { usePendingTransactions } from "../../../hooks/usePendingTransactions";
import { useNavigate } from "react-router-dom";

// Pulsing animation for status indicator
const pulse = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const SystemHealth = () => {
  const navigate = useNavigate();
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [securityOpen, setSecurityOpen] = useState(false);
  const [systemStats, setSystemStats] = useState({
    database: { status: 'checking', projectId: '' },
    activeUsers: { status: 'checking', count: 0, last24h: 0 },
    pendingTransactions: { status: 'checking', deposits: 0, withdrawals: 0 },
    recentActivity: {
      status: 'checking',
      newDeposits: 0, newWithdrawals: 0,
      completedDeposits: 0, completedWithdrawals: 0,
      lastActivity: null,
    },
    autoPayFunction: { status: 'checking', lastRun: null, pendingAutoApprove: 0, pendingAutoPay: 0 },
    walletAudit: { status: 'checking', recent: 0 },
  });

  const { pendingDeposits, pendingWithdrawals, loading: pendingLoading } = usePendingTransactions();

  // Check Database Connection
  const checkDatabase = async () => {
    try {
      const projectId = DATABASE.app.options.projectId;
      if (projectId === 'fidduxai-rowy') {
        setSystemStats(prev => ({
          ...prev,
          database: { status: 'healthy', projectId }
        }));
      } else {
        setSystemStats(prev => ({
          ...prev,
          database: { status: 'error', projectId }
        }));
      }
    } catch (error) {
      setSystemStats(prev => ({
        ...prev,
        database: { status: 'error', projectId: 'unknown' }
      }));
    }
  };

  // Check Active Users (users with any transaction in last 30 days)
  const checkActiveUsers = async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

      // No orderBy to avoid composite index requirement
      const snap = await getDocs(query(
        collection(DATABASE, 'transection_requests'),
        where('request_type', 'in', [0, 1]),
        limit(1000)
      ));

      const users30d = new Set();
      const users24h = new Set();

      snap.docs.forEach(doc => {
        const d = doc.data();
        const uid = d.user_id;
        const dt = d.date?.toDate();
        if (uid && dt) {
          if (dt >= thirtyDaysAgo) users30d.add(uid);
          if (dt >= twentyFourHoursAgo) users24h.add(uid);
        }
      });

      setSystemStats(prev => ({
        ...prev,
        activeUsers: { status: 'healthy', count: users30d.size, last24h: users24h.size }
      }));
    } catch (error) {
      console.error('Error checking active users:', error);
      setSystemStats(prev => ({
        ...prev,
        activeUsers: { status: 'error', count: 0, last24h: 0 }
      }));
    }
  };

  const asDate = (value) => {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  /**
   * Transaction Activity (last 24h)
   *
   * Reads recent documents by each relevant timestamp field, then deduplicates
   * by doc id. This catches:
   * - new requests submitted today (`date`)
   * - deposits approved today (`request_approval_time` / `conclusion_date`)
   * - withdrawals paid/rejected/cancelled today, even if requested days ago
   */
  const checkRecentActivity = async () => {
    try {
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

      const txCollection = collection(DATABASE, 'transection_requests');
      const [byRequestDate, byApprovalTime, byConclusionDate] = await Promise.all([
        getDocs(query(txCollection, orderBy('date', 'desc'), limit(300))),
        getDocs(query(txCollection, orderBy('request_approval_time', 'desc'), limit(300))),
        getDocs(query(txCollection, orderBy('conclusion_date', 'desc'), limit(300))),
      ]);

      const docsById = new Map();
      [byRequestDate, byApprovalTime, byConclusionDate].forEach(snap => {
        snap.docs.forEach(docSnap => {
          docsById.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
      });

      let newDeposits = 0;
      let newWithdrawals = 0;
      let completedDeposits = 0;
      let completedWithdrawals = 0;
      let lastActivity = null;

      const trackActivity = (dt) => {
        if (dt && (!lastActivity || dt > lastActivity)) lastActivity = dt;
      };

      docsById.forEach(d => {
        if (d.request_type !== 0 && d.request_type !== 1) return;

        const requestDate = asDate(d.date);
        const finalDate = asDate(d.conclusion_date) || asDate(d.request_approval_time);

        if (requestDate && requestDate >= twentyFourHoursAgo) {
          if (d.request_type === 0) newDeposits++;
          if (d.request_type === 1) newWithdrawals++;
          trackActivity(requestDate);
        }

        if (finalDate && finalDate >= twentyFourHoursAgo) {
          if (d.request_type === 0 && d.request_status === 2) completedDeposits++;
          if (d.request_type === 1 && [3, 4, 5].includes(d.request_status)) completedWithdrawals++;
          if (
            (d.request_type === 0 && d.request_status === 2) ||
            (d.request_type === 1 && [3, 4, 5].includes(d.request_status))
          ) {
            trackActivity(finalDate);
          }
        }
      });

      setSystemStats(prev => ({
        ...prev,
        recentActivity: {
          // Zero activity is informational, not a system health problem.
          status: 'healthy',
          newDeposits, newWithdrawals,
          completedDeposits, completedWithdrawals,
          lastActivity,
        }
      }));
    } catch (error) {
      console.error('Error checking recent activity:', error);
      setSystemStats(prev => ({
        ...prev,
        recentActivity: {
          status: 'error',
          newDeposits: 0, newWithdrawals: 0,
          completedDeposits: 0, completedWithdrawals: 0,
          lastActivity: null,
        }
      }));
    }
  };

  // Update pending transactions status
  useEffect(() => {
    if (!pendingLoading) {
      const totalPending = pendingDeposits + pendingWithdrawals;
      setSystemStats(prev => ({
        ...prev,
        pendingTransactions: {
          status: totalPending > 0 ? 'warning' : 'healthy',
          deposits: pendingDeposits,
          withdrawals: pendingWithdrawals
        }
      }));
    }
  }, [pendingDeposits, pendingWithdrawals, pendingLoading]);

  // Check Auto-Pay function health by examining pending auto-pay transactions
  const checkAutoPayFunction = async () => {
    try {
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);

      // status=0 deposits pending auto-accept (avoid orderBy to skip composite index)
      const autoApproveQuery = query(
        collection(DATABASE, 'transection_requests'),
        where('request_type', '==', 1),
        where('status', '==', 0),
        limit(100)
      );
      // status=5 withdrawals marked ready-to-pay (no orderBy)
      const autoPayReadyQuery = query(
        collection(DATABASE, 'transection_requests'),
        where('request_type', '==', 2),
        where('status', '==', 5),
        limit(100)
      );

      const [approveSnap, paySnap] = await Promise.all([
        getDocs(autoApproveQuery),
        getDocs(autoPayReadyQuery),
      ]);

      const pendingAutoApprove = approveSnap.docs.filter(d => {
        const date = d.data().date?.toDate();
        return date && date >= oneDayAgo;
      }).length;

      const pendingAutoPay = paySnap.size;

      setSystemStats(prev => ({
        ...prev,
        autoPayFunction: {
          status: pendingAutoPay > 20 ? 'warning' : 'healthy',
          lastRun: new Date(),
          pendingAutoApprove,
          pendingAutoPay,
        }
      }));
    } catch (error) {
      console.error('checkAutoPayFunction error:', error);
      setSystemStats(prev => ({
        ...prev,
        autoPayFunction: {
          status: 'error',
          lastRun: null,
          pendingAutoApprove: 0,
          pendingAutoPay: 0,
          errorMsg: error?.message || 'Query failed',
        }
      }));
    }
  };

  // Check wallet audit log (recent wallet changes in last 48h)
  const checkWalletAudit = async () => {
    try {
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

      const auditQuery = query(
        collection(DATABASE, 'wallet_audit_log'),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      const snap = await getDocs(auditQuery);
      const recent = snap.docs.filter(d => {
        const ts = d.data().timestamp?.toDate();
        return ts && ts >= twoDaysAgo;
      }).length;

      setSystemStats(prev => ({
        ...prev,
        walletAudit: { status: 'healthy', recent }
      }));
    } catch {
      setSystemStats(prev => ({
        ...prev,
        walletAudit: { status: 'warning', recent: 0 }
      }));
    }
  };

  // Initial load
  useEffect(() => {
    handleRefresh();
  }, []);

  const handleRefresh = () => {
    setLastRefresh(new Date());
    checkDatabase();
    checkActiveUsers();
    checkRecentActivity();
    checkAutoPayFunction();
    checkWalletAudit();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return '#00c820'; // Green
      case 'warning': return '#ff9800'; // Yellow/Orange
      case 'error': return '#f52a2d'; // Red
      default: return '#9e9e9e';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon sx={{ color: '#00c820' }} />;
      case 'warning': return <WarningIcon sx={{ color: '#ff9800' }} />;
      case 'error': return <ErrorIcon sx={{ color: '#f52a2d' }} />;
      default: return <RefreshIcon sx={{ color: '#9e9e9e' }} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'healthy': return 'System Healthy';
      case 'warning': return 'Attention Required';
      case 'error': return 'Urgent Attention';
      default: return 'Checking...';
    }
  };

  const StatCard = ({ title, icon, status, children }) => (
    <Card
      sx={{
        height: '100%',
        background: 'linear-gradient(135deg, #353535 0%, #1e1e1e 100%)',
        border: `1px solid rgba(255, 255, 255, 0.1)`,
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px rgba(0, 0, 0, 0.4)`,
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {React.cloneElement(icon, { sx: { color: '#fff', fontSize: 24 } })}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
              {title}
            </Typography>
          </Box>
          {getStatusIcon(status)}
        </Box>
        <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        {children}
      </CardContent>
    </Card>
  );

  const overallStatus = () => {
    const statuses = Object.values(systemStats).map(s => s.status);
    if (statuses.includes('error')) return 'error';
    if (statuses.includes('warning')) return 'warning';
    if (statuses.every(s => s === 'healthy')) return 'healthy';
    return 'checking';
  };

  // Security Architecture Dialog
  const SECURITY_LAYERS = [
    {
      layer: "Layer 1", num: "01",
      title: "API Keys & Exchange Credentials",
      color: "#ff5c70", bgColor: "rgba(255,92,112,0.08)",
      icon: "🔑",
      status: "Active",
      items: [
        { label: "Storage", value: "Google Cloud Secret Manager — never in code, .env or Firestore" },
        { label: "Runtime access", value: "Cloud Functions read keys via secretFunction binding at invocation" },
        { label: "IP Whitelist", value: "Exchange accounts (Bitget/Bybit) locked to Firebase Cloud Run IPs only" },
        { label: "Key scope", value: "Read-only where possible; withdraw keys limited to known destination addresses" },
        { label: "Rotation", value: "Every 90 days, or immediately on any suspected compromise" },
      ],
    },
    {
      layer: "Layer 2", num: "02",
      title: "Liquidity Wallets (Operational)",
      color: "#ffd54f", bgColor: "rgba(255,213,79,0.07)",
      icon: "💰",
      status: "Partial",
      items: [
        { label: "Hot wallet", value: "Holds minimum balance needed for current auto-pay batch only" },
        { label: "Treasury", value: "Main funds in exchange cold wallets — NOT in hot operational wallets" },
        { label: "Insufficient balance", value: "Auto-pay flags transaction for manual review if liquidity wallet is empty" },
        { label: "Daily cap", value: "fund_rules/active → auto_pay.daily_cap limits total daily auto-pay exposure" },
        { label: "Recommended", value: "Hardware wallet (Ledger) + Gnosis Safe multi-sig for treasury" },
      ],
    },
    {
      layer: "Layer 3", num: "03",
      title: "Wallet Security Cooldowns",
      color: "#7eb8ff", bgColor: "rgba(126,184,255,0.07)",
      icon: "⏱️",
      status: "Active",
      items: [
        { label: "New wallet", value: "24h cooldown via auto_pay_eligible_from — auto-pay skips until elapsed" },
        { label: "Edited wallet", value: "24h suspension via edit_suspended_until + admin email alert sent automatically" },
        { label: "Address immutability", value: "Wallet address field locked in Firestore rules after creation" },
        { label: "Audit trail", value: "edit_history array (max 20 entries) + wallet_audit_log collection" },
        { label: "Write restriction", value: "wallet_audit_log is write-only from Cloud Functions — no direct client writes" },
      ],
    },
    {
      layer: "Layer 4", num: "04",
      title: "Transaction Safeguards",
      color: "#2af598", bgColor: "rgba(42,245,152,0.07)",
      icon: "🛡️",
      status: "Active",
      items: [
        { label: "Deposits", value: "Admin manual approval required — funds not credited until reviewed" },
        { label: "Auto-pay threshold", value: "Withdrawals above max_amount are sent to manual queue, never auto-processed" },
        { label: "Daily cap", value: "daily_cap in fund_rules — auto-pay stops for the day when cap is reached" },
        { label: "Time window", value: "Withdrawal expires if not accepted within max_window_hours" },
        { label: "On-chain payment", value: "Status=5 (ready-to-pay) is manual until exchange API integration is live" },
      ],
    },
    {
      layer: "Layer 5", num: "05",
      title: "Access Control & Audit",
      color: "#b47cff", bgColor: "rgba(180,124,255,0.07)",
      icon: "🔒",
      status: "Active",
      items: [
        { label: "Auth", value: "Firebase Authentication — only whitelisted admin UIDs can access admin panel" },
        { label: "Firestore rules", value: "Role-based access; wallet_audit_log is read-only for admins" },
        { label: "Admin logging", value: "Every admin action logged with email + timestamp" },
        { label: "Email alerts", value: "Admin notified on wallet creation, edits, large deposits/withdrawals" },
        { label: "Future", value: "Dual-approval for high-value transactions above configurable threshold" },
      ],
    },
  ];

  const AUTO_PAY_FLOW = [
    { step: "1", label: "Client submits withdrawal request in app", color: "#7eb8ff" },
    { step: "2", label: "Cloud Function waits accept_delay (e.g. 12h) → checks amount threshold & wallet cooldowns → auto-accepts if OK", color: "#7eb8ff" },
    { step: "3", label: "Wallet checks: new wallet (<24h) or recently edited? → blocked, manual review required", color: "#ffd54f" },
    { step: "4", label: "Daily cap check: total auto-paid today < daily_cap? → proceed, else skip for today", color: "#ffd54f" },
    { step: "5", label: "Wait pay_delay (e.g. 6h) → mark status=5 (ready-to-pay) → admin notified by email", color: "#2af598" },
    { step: "6", label: "Admin manually sends on-chain payment (or future: exchange API auto-executes)", color: "#ff5c70" },
  ];

  const SecurityArchitectureDialog = () => (
    <Dialog
      open={securityOpen}
      onClose={() => setSecurityOpen(false)}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          bgcolor: "#0e0e1a",
          color: "#fff",
          borderRadius: { xs: 0, md: 3 },
          border: "1px solid rgba(255,255,255,0.1)",
          minHeight: "80vh",
        }
      }}
    >
      {/* Header */}
      <Box sx={{ background: "linear-gradient(135deg, rgba(42,245,152,0.12) 0%, rgba(126,184,255,0.08) 100%)", borderBottom: "1px solid rgba(255,255,255,0.1)", px: 4, py: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: "rgba(42,245,152,0.15)", borderRadius: 2, border: "1px solid rgba(42,245,152,0.3)" }}>
              <ShieldIcon sx={{ color: "#2af598", fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: "#fff", lineHeight: 1.2 }}>
                Security Architecture
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mt: 0.25 }}>
                5-layer defence model · Fiddux Admin Platform
              </Typography>
            </Box>
          </Box>
          <Button onClick={() => setSecurityOpen(false)} sx={{ color: "rgba(255,255,255,0.5)", minWidth: 0, p: 1 }}>✕</Button>
        </Box>

        {/* Layer overview bar */}
        <Box sx={{ display: "flex", gap: 1, mt: 2.5, flexWrap: "wrap" }}>
          {SECURITY_LAYERS.map(({ num, title, color, status }) => (
            <Box key={num} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.5, bgcolor: `${color}15`, border: `1px solid ${color}40`, borderRadius: 2 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: status === "Active" ? color : "#ffd54f" }} />
              <Typography variant="caption" sx={{ color, fontWeight: 700, fontSize: 11 }}>L{num}</Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>{title.split(" ")[0]} {title.split(" ")[1] || ""}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Grid container>
          {/* Left: Layer cards */}
          <Grid item xs={12} md={7} sx={{ p: 3, borderRight: { md: "1px solid rgba(255,255,255,0.07)" } }}>
            <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.4)", letterSpacing: 2, fontSize: 11, display: "block", mb: 2 }}>DEFENCE LAYERS</Typography>
            {SECURITY_LAYERS.map(({ num, layer, title, color, bgColor, icon, status, items }) => (
              <Box key={num} sx={{ mb: 2.5, p: 2.5, bgcolor: bgColor, border: `1px solid ${color}25`, borderLeft: `4px solid ${color}`, borderRadius: "0 12px 12px 0" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography sx={{ fontSize: 20 }}>{icon}</Typography>
                    <Box>
                      <Typography variant="overline" sx={{ color, fontSize: 10, letterSpacing: 1.5, lineHeight: 1, display: "block" }}>{layer}</Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2 }}>{title}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ px: 1, py: 0.25, bgcolor: status === "Active" ? "rgba(42,245,152,0.15)" : "rgba(255,213,79,0.15)", borderRadius: 1, border: `1px solid ${status === "Active" ? "rgba(42,245,152,0.3)" : "rgba(255,213,79,0.3)"}` }}>
                    <Typography variant="caption" sx={{ color: status === "Active" ? "#2af598" : "#ffd54f", fontWeight: 700, fontSize: 10 }}>
                      {status === "Active" ? "✓ ACTIVE" : "⚡ PARTIAL"}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {items.map(({ label, value }, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Typography variant="caption" sx={{ color, fontWeight: 700, minWidth: 110, flexShrink: 0, mt: 0.1, fontSize: 11 }}>{label}</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.5, fontSize: 13 }}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Grid>

          {/* Right: Auto-Pay Flow */}
          <Grid item xs={12} md={5} sx={{ p: 3, bgcolor: "rgba(0,0,0,0.2)" }}>
            <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.4)", letterSpacing: 2, fontSize: 11, display: "block", mb: 2 }}>AUTO-PAY FLOW</Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", display: "block", mb: 2.5 }}>
              Current implementation — blockchain payment is manual (step 6) until exchange API integration
            </Typography>
            {AUTO_PAY_FLOW.map(({ step, label, color }, i, arr) => (
              <Box key={step} sx={{ display: "flex", gap: 1.5, mb: i < arr.length - 1 ? 0 : 0, position: "relative" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: `${color}20`, border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>
                    <Typography variant="caption" fontWeight={800} sx={{ color, fontSize: 12 }}>{step}</Typography>
                  </Box>
                  {i < arr.length - 1 && (
                    <Box sx={{ width: 2, flexGrow: 1, minHeight: 24, bgcolor: `${color}30`, my: 0.5 }} />
                  )}
                </Box>
                <Box sx={{ pb: i < arr.length - 1 ? 0 : 0, pt: 0.5, mb: i < arr.length - 1 ? 2 : 0 }}>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.6, fontSize: 13 }}>{label}</Typography>
                </Box>
              </Box>
            ))}

            {/* Status summary */}
            <Box sx={{ mt: 3, p: 2, bgcolor: "rgba(42,245,152,0.07)", border: "1px solid rgba(42,245,152,0.2)", borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#2af598", mb: 1.5 }}>Current Security Status</Typography>
              {[
                { label: "Secret Manager", ok: true },
                { label: "IP Whitelist (Exchange)", ok: false, note: "Verify in Bitget/Bybit" },
                { label: "Wallet Cooldowns", ok: true },
                { label: "Firestore Rules", ok: true },
                { label: "Admin Notifications", ok: true },
                { label: "Exchange API Auto-Pay", ok: false, note: "Not yet integrated" },
                { label: "Hardware Wallet Treasury", ok: false, note: "Recommended, not yet set up" },
              ].map(({ label, ok, note }) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                  <Typography sx={{ fontSize: 14 }}>{ok ? "✅" : "⚠️"}</Typography>
                  <Typography variant="body2" sx={{ color: ok ? "rgba(255,255,255,0.8)" : "#ffd54f", fontSize: 13 }}>{label}</Typography>
                  {note && <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>— {note}</Typography>}
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid rgba(255,255,255,0.08)", p: 2, bgcolor: "rgba(0,0,0,0.2)" }}>
        <Button onClick={() => setSecurityOpen(false)} sx={{ color: "rgba(255,255,255,0.6)", "&:hover": { color: "#fff" } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Get detailed issues for warning/error status
  const getSystemIssues = () => {
    const issues = [];
    
    // Check database
    if (systemStats.database.status === 'error') {
      issues.push({
        severity: 'error',
        component: 'Database',
        message: `Wrong database detected: ${systemStats.database.projectId}. Expected: fidduxai-rowy`,
        action: 'Check Firebase configuration immediately'
      });
    }

    // Check active users
    if (systemStats.activeUsers.status === 'error') {
      issues.push({
        severity: 'error',
        component: 'Active Users',
        message: 'Failed to fetch active users data',
        action: 'Check Firestore connection and permissions'
      });
    }

    // Check pending transactions
    if (systemStats.pendingTransactions.status === 'warning') {
      const total = systemStats.pendingTransactions.deposits + systemStats.pendingTransactions.withdrawals;
      issues.push({
        severity: 'warning',
        component: 'Pending Transactions',
        message: `${total} pending transaction(s) awaiting review (${systemStats.pendingTransactions.deposits} deposits, ${systemStats.pendingTransactions.withdrawals} withdrawals)`,
        action: 'Review and process pending deposits and withdrawals'
      });
    }

    // Check recent activity
    if (systemStats.recentActivity.status === 'warning') {
      issues.push({
        severity: 'warning',
        component: 'Transaction Activity (24h)',
        message: 'No new requests and no completed transactions in the last 24 hours.',
        action: 'This is informational — no action required if the platform is in a quiet period. Check Deposits and Withdrawals pages to confirm.'
      });
    }

    if (systemStats.recentActivity.status === 'error') {
      issues.push({
        severity: 'error',
        component: 'Transaction Activity',
        message: 'Failed to fetch transaction activity data',
        action: 'Check Firestore connection and query permissions'
      });
    }

    // Check Auto-Pay Engine
    if (systemStats.autoPayFunction.status === 'error') {
      issues.push({
        severity: 'error',
        component: 'Auto-Pay Engine',
        message: `Cloud Function query failed${systemStats.autoPayFunction.errorMsg ? `: ${systemStats.autoPayFunction.errorMsg}` : ''}`,
        action: 'Check Firestore composite indexes for transection_requests (request_type + status). May need to create index in Firebase Console.'
      });
    }
    if (systemStats.autoPayFunction.status === 'warning') {
      issues.push({
        severity: 'warning',
        component: 'Auto-Pay Engine',
        message: `${systemStats.autoPayFunction.pendingAutoPay} withdrawals marked ready-to-pay (status=5) awaiting manual on-chain payment`,
        action: 'Review and process ready-to-pay withdrawals in Funds Management → Withdrawals'
      });
    }

    // Check Wallet Audit
    if (systemStats.walletAudit.status === 'warning') {
      issues.push({
        severity: 'warning',
        component: 'Wallet Audit Log',
        message: 'Could not query wallet_audit_log — collection may not exist yet',
        action: 'Add or edit a wallet to create the collection, or check Firestore security rules'
      });
    }

    return issues;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
          borderRadius: '16px',
          p: 3,
          mb: 3,
          color: '#fff',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <HealthAndSafetyIcon sx={{ fontSize: 48, color: '#fff' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#fff' }}>
                System Health Monitor
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, color: '#fff' }}>
                Real-time monitoring of Fiddux Admin Panel
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Status Indicator with Pulsing Light */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, 
              background: 'rgba(0, 0, 0, 0.3)', 
              px: 2, 
              py: 1, 
              borderRadius: '20px',
              border: `1px solid ${getStatusColor(overallStatus())}`
            }}>
              <FiberManualRecordIcon 
                sx={{ 
                  color: getStatusColor(overallStatus()),
                  fontSize: 16,
                  animation: `${pulse} 2s ease-in-out infinite`
                }} 
              />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                {getStatusText(overallStatus())}
              </Typography>
            </Box>
            <Button
              onClick={() => navigate('/admin/security-architecture')}
              size="small"
              startIcon={<ShieldIcon />}
              variant="outlined"
              sx={{
                borderColor: '#2af598',
                color: '#2af598',
                fontWeight: 700,
                fontSize: 12,
                px: 2,
                '&:hover': { bgcolor: 'rgba(42,245,152,0.1)', borderColor: '#2af598' }
              }}
            >
              Security Plan
            </Button>
            <Tooltip title="Refresh all metrics">
              <IconButton
                onClick={handleRefresh}
                sx={{
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  '&:hover': { background: 'rgba(255,255,255,0.2)' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.7, color: '#fff' }}>
          Last updated: {lastRefresh.toLocaleTimeString()}
        </Typography>
      </Paper>

      {/* System Issues Alert */}
      {(overallStatus() === 'warning' || overallStatus() === 'error') && (
        <Alert 
          severity={overallStatus() === 'error' ? 'error' : 'warning'}
          sx={{ 
            mb: 3, 
            borderRadius: '12px',
            border: `2px solid ${getStatusColor(overallStatus())}`,
            background: overallStatus() === 'error' 
              ? 'rgba(245, 42, 45, 0.1)' 
              : 'rgba(255, 152, 0, 0.1)'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {overallStatus() === 'error' ? '🔴 Urgent Attention Required' : '🟡 Attention Required'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {getSystemIssues().map((issue, index) => (
              <Box 
                key={index}
                sx={{ 
                  background: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '8px',
                  p: 2,
                  borderLeft: `4px solid ${issue.severity === 'error' ? '#f52a2d' : '#ff9800'}`
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {issue.component}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                  {issue.message}
                </Typography>
                <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  → {issue.action}
                </Typography>
              </Box>
            ))}
          </Box>
        </Alert>
      )}

      {/* All Systems Healthy Message */}
      {overallStatus() === 'healthy' && (
        <Alert 
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ 
            mb: 3, 
            borderRadius: '12px',
            border: '2px solid #00c820',
            background: 'rgba(0, 200, 32, 0.1)'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🟢 All Systems Operational
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            All components are functioning normally. No action required.
          </Typography>
        </Alert>
      )}

      {/* Metrics Grid */}
      <Grid container spacing={3}>
        {/* Database Status */}
        <Grid item xs={12} md={6} lg={4}>
          <StatCard
            title="Database"
            icon={<StorageIcon />}
            status={systemStats.database.status}
          >
            <Typography variant="body2" sx={{ color: '#aaa' }} gutterBottom>
              Connection Status
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
              {systemStats.database.status === 'healthy' ? 'Connected' : 'Error'}
            </Typography>
            <Chip
              label={systemStats.database.projectId}
              size="small"
              sx={{
                background: systemStats.database.projectId === 'fidduxai-rowy' ? 'rgba(0, 200, 32, 0.2)' : 'rgba(245, 42, 45, 0.2)',
                color: systemStats.database.projectId === 'fidduxai-rowy' ? '#00c820' : '#f52a2d',
                fontWeight: 600,
                border: `1px solid ${systemStats.database.projectId === 'fidduxai-rowy' ? '#00c820' : '#f52a2d'}`
              }}
            />
          </StatCard>
        </Grid>

        {/* Active Users */}
        <Grid item xs={12} md={6} lg={4}>
          <StatCard
            title="Active Users"
            icon={<PeopleIcon />}
            status={systemStats.activeUsers.status}
          >
            <Typography variant="body2" sx={{ color: '#aaa' }} gutterBottom>
              Last 30 Days
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
              {systemStats.activeUsers.count}
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {systemStats.activeUsers.last24h} active in last 24h
            </Typography>
          </StatCard>
        </Grid>

        {/* Pending Transactions */}
        <Grid item xs={12} md={6} lg={4}>
          <StatCard
            title="Pending Actions"
            icon={<AccountBalanceWalletIcon />}
            status={systemStats.pendingTransactions.status}
          >
            <Typography variant="body2" sx={{ color: '#aaa' }} gutterBottom>
              Awaiting Review
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                  {systemStats.pendingTransactions.deposits}
                </Typography>
                <Typography variant="caption" sx={{ color: '#aaa' }}>
                  Deposits
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                  {systemStats.pendingTransactions.withdrawals}
                </Typography>
                <Typography variant="caption" sx={{ color: '#aaa' }}>
                  Withdrawals
                </Typography>
              </Box>
            </Box>
          </StatCard>
        </Grid>

        {/* Transaction Activity — Last 24h */}
        <Grid item xs={12} md={6} lg={4}>
          <StatCard
            title="Activity (24h)"
            icon={<TrendingUpIcon />}
            status={systemStats.recentActivity.status}
          >
            {/* New requests row */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#aaa', textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 }}>
                New Requests
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#7eb8ff', lineHeight: 1.1 }}>
                    {systemStats.recentActivity.newDeposits ?? 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: 10 }}>Deposits</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#7eb8ff', lineHeight: 1.1 }}>
                    {systemStats.recentActivity.newWithdrawals ?? 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: 10 }}>Withdrawals</Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 1 }} />

            {/* Completed row */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#aaa', textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 }}>
                Finalized Today
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2af598', lineHeight: 1.1 }}>
                    {systemStats.recentActivity.completedDeposits ?? 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: 10 }}>Approved</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2af598', lineHeight: 1.1 }}>
                    {systemStats.recentActivity.completedWithdrawals ?? 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#aaa', fontSize: 10 }}>Withdrawals</Typography>
                </Box>
              </Box>
            </Box>

            <Typography variant="caption" sx={{ color: '#555', fontSize: 10 }}>
              {systemStats.recentActivity.lastActivity
                ? `Last action: ${systemStats.recentActivity.lastActivity.toLocaleTimeString()}`
                : 'No activity recorded in last 24h'}
            </Typography>
          </StatCard>
        </Grid>

        {/* Auto-Pay Function */}
        <Grid item xs={12} md={6} lg={4}>
          <StatCard
            title="Auto-Pay Engine"
            icon={<AutorenewIcon />}
            status={systemStats.autoPayFunction.status}
          >
            <Typography variant="body2" sx={{ color: '#aaa' }} gutterBottom>
              processAutoPayWithdrawal
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                  {systemStats.autoPayFunction.pendingAutoApprove}
                </Typography>
                <Typography variant="caption" sx={{ color: '#aaa' }}>Pending accept</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: systemStats.autoPayFunction.pendingAutoPay > 10 ? '#ff9800' : '#fff' }}>
                  {systemStats.autoPayFunction.pendingAutoPay}
                </Typography>
                <Typography variant="caption" sx={{ color: '#aaa' }}>Ready to pay</Typography>
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              Runs every 5 min via Cloud Scheduler
            </Typography>
          </StatCard>
        </Grid>

        {/* Wallet Audit */}
        <Grid item xs={12} md={6} lg={4}>
          <StatCard
            title="Wallet Audit Log"
            icon={<AccountBalanceWalletIcon />}
            status={systemStats.walletAudit.status}
          >
            <Typography variant="body2" sx={{ color: '#aaa' }} gutterBottom>
              notifyWalletChanged
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
              {systemStats.walletAudit.recent}
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              Wallet changes in last 48h (new / edited)
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* System Information */}
      <Card sx={{ mt: 3, borderRadius: '12px' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SecurityIcon sx={{ color: '#fff' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
              System Information
            </Typography>
          </Box>
          <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Environment
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff' }}>
                Production
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Firebase Project
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff' }}>
                {systemStats.database.projectId}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Admin Panel Version
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff' }}>
                2.0.0
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Health Check Legend */}
      <Card sx={{ mt: 3, borderRadius: '12px' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#fff' }}>
            Status Indicator Legend
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FiberManualRecordIcon sx={{ color: '#00c820', fontSize: 16 }} />
              <Typography variant="body2" sx={{ color: '#aaa' }}>Green - System Healthy</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FiberManualRecordIcon sx={{ color: '#ff9800', fontSize: 16 }} />
              <Typography variant="body2" sx={{ color: '#aaa' }}>Yellow - Attention Required</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FiberManualRecordIcon sx={{ color: '#f52a2d', fontSize: 16 }} />
              <Typography variant="body2" sx={{ color: '#aaa' }}>Red - Urgent Attention</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <SecurityArchitectureDialog />
    </Box>
  );
};

export default SystemHealth;
